import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import AdminReportsService from '../services/AdminReportsService';
import { RequestServiceResponse } from '@tcc-online/request-service';
import Report from '../types/Report';
import Result from '../types/Result';

const NAME = 'AdminReports';

const getReports = createAsyncThunk(
    `${NAME}/getReports`,
    AdminReportsService.getReports,
);
const getResult = createAsyncThunk(
    `${NAME}/getResult`,
    AdminReportsService.getResult,
);

export type AdminReportsState = {
    reports: IBaseState<Report[]>;
    result: IBaseState<Result | null>;
};

const initialState: AdminReportsState = {
    reports: { data: [], loading: false },
    result: { data: null, loading: false },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            getReports.pending,
            (state: AdminReportsState): void => {
                state.reports.loading = true;
            },
        );
        builder.addCase(
            getReports.fulfilled,
            (
                state: AdminReportsState,
                action: PayloadAction<RequestServiceResponse<Report[]>>,
            ): void => {
                state.reports.data = action.payload.data;
                state.reports.loading = false;
            },
        );
        builder.addCase(
            getReports.rejected,
            (state: AdminReportsState): void => {
                state.reports.loading = false;
            },
        );

        builder.addCase(getResult.pending, (state: AdminReportsState): void => {
            state.result.loading = true;
        });
        builder.addCase(
            getResult.fulfilled,
            (
                state: AdminReportsState,
                action: PayloadAction<RequestServiceResponse<Result>>,
            ): void => {
                state.result.data = action.payload.data;
                state.result.loading = false;
            },
        );
        builder.addCase(
            getResult.rejected,
            (state: AdminReportsState): void => {
                state.result.loading = false;
            },
        );
    },
});

const state = (state: AppState): AdminReportsState => state.admin.reports;

const reportId = (_: AppState, reportId: number) => reportId;
const all = createSelector(state, (state: AdminReportsState) => state);
const reports = createSelector(
    all,
    (state: AdminReportsState) => state.reports.data,
);

const AdminReportsSlice = {
    reducer: slice.reducer,
    actions: {
        getReports,
        getResult,
    },
    state: {
        all: all,
        reports: createSelector(
            all,
            (state: AdminReportsState) => state.reports,
        ),
        detail: createSelector(
            [reports, reportId],
            (reports: Report[], reportId: number) =>
                reports.find((report: Report) => report.id === reportId),
        ),
        result: createSelector(all, (state: AdminReportsState) => state.result),
    },
};

export default AdminReportsSlice;
